import React from 'react';
import { Alert, Button, CircularProgress, Stack, TextField } from '@mui/material';
import { CheckCircleOutline, LockResetOutlined } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import AuthShell from '../components/AuthShell';
import { AuthAPI } from '../api/auth';
import { ROUTES } from '../routes';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!token) return;
    AuthAPI.verifyResetToken(token).catch(() => setError('The reset link is invalid or expired.'));
  }, [token]);

  React.useEffect(() => {
    if (!msg) return undefined;
    const timeout = window.setTimeout(() => navigate(ROUTES.LOGIN, { replace: true }), 1800);
    return () => window.clearTimeout(timeout);
  }, [msg, navigate]);

  const submit = async (event) => {
    event.preventDefault();
    setMsg('');
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await AuthAPI.resetWithToken(token, newPassword);
      setMsg('Password updated successfully. Redirecting you to sign in…');
    } catch (err) {
      setError(err?.response?.data?.message || 'Reset failed. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Choose a new password for your account."
      sideTitle="Secure recovery"
      sideBody="Reset tokens are short-lived and can only be used once."
    >
      <Stack component="form" spacing={2} onSubmit={submit}>
        {msg ? <Alert severity="success">{msg}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <TextField
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />
        <TextField
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />

        <Button type="submit" variant="contained" disabled={loading || !token || Boolean(msg)} startIcon={<LockResetOutlined />}>
          {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Update password'}
        </Button>

        <Button component={RouterLink} to={ROUTES.LOGIN} variant="outlined" startIcon={<CheckCircleOutline />}>
          Back to sign in
        </Button>
      </Stack>
    </AuthShell>
  );
}
